// Port of webapp/src/components/propertyValueElement.tsx (the read-only
// renderer). Editors live in ./cardDetail/propertyEditors.tsx.

import {ReactElement} from 'react'
import classnames from 'classnames'
import {IPropertyTemplate} from '../types/board'
import {Card} from '../types/card'
import {IUser} from '../types/user'
import {createDatePropertyFromString, optionColorCssVar, parseArrayValue} from '../state/properties'
import {Utils} from '../common/utils'
import {Icon, IconName, propertyIcons} from './common/icons'

type Props = {
    template: IPropertyTemplate
    card: Card
    usersById: Record<string, IUser>
    className?: string
    /** kanban/gallery chips vs. table cells */
    asChip?: boolean
}

const defaultTextForType: Record<string, string> = {
    number: '数字',
    date: '日期',
    person: '人员',
    multiPerson: '人员',
    url: '链接',
    email: '邮箱',
    phone: '电话',
    text: '文本',
    select: '单选',
    multiSelect: '单选',
    checkbox: '复选框',
    createdTime: '创建时间',
    updatedTime: '更新时间',
    createdBy: '创建人',
    updatedBy: '更新人',
    file: '附件',
    unknown: '属性',
}

export function propertyDisplayName(template: IPropertyTemplate): string {
    return template.name || defaultTextForType[template.type] || '属性'
}

export function propertyIcon(template: IPropertyTemplate): IconName {
    return propertyIcons[template.type] || 'help'
}

function avatarInitial(user: IUser | undefined): string {
    if (!user) {
        return '?'
    }
    return (user.firstname.charAt(0) + (user.lastname.charAt(0) || '')).toUpperCase()
}

export default function PropertyValue({template, card, usersById, className, asChip = true}: Props): ReactElement | null {
    const raw = card.fields.properties[template.id]
    const isEmpty = raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0)

    // upstream chips/cells render nothing for an unset property
    if (isEmpty) {
        return null
    }

    switch (template.type) {
    case 'select': {
        const option = template.options.find((o) => o.id === raw)
        if (!option) {
            return isEmpty ? null : <span className={classnames('propValue truncate', className)}>{String(raw)}</span>
        }
        return (
            <span
                className={classnames('propValue', !asChip && 'bg-transparent px-0', className)}
                style={asChip ? {backgroundColor: optionColorCssVar(option.color)} : undefined}
                title={option.value}
            >
                {!asChip && <i className='mr-2 inline-block h-2 w-2 rounded-full' style={{backgroundColor: optionColorCssVar(option.color)}}/>}
                <span className='truncate'>{option.value}</span>
            </span>
        )
    }
    case 'multiSelect': {
        const ids = parseArrayValue(raw)
        if (!ids.length) {
            return null
        }
        return (
            <span className={classnames('flex flex-wrap items-center gap-1', className)}>
                {ids.map((id) => {
                    const option = template.options.find((o) => o.id === id)
                    if (!option) {
                        return null
                    }
                    return (
                        <span
                            key={id}
                            className='propValue truncate'
                            style={{backgroundColor: optionColorCssVar(option.color)}}
                        >
                            {option.value}
                        </span>
                    )
                })}
            </span>
        )
    }
    case 'date': {
        const dp = createDatePropertyFromString(raw)
        if (!dp.from && !dp.to) {
            return null
        }
        const fmt = (ms: number): string => {
            const d = new Date(ms)
            const today = new Date()
            const sameYear = d.getFullYear() === today.getFullYear()
            return new Intl.DateTimeFormat(Utils.getLocale(), {
                month: 'short',
                day: 'numeric',
                ...(sameYear ? {} : {year: 'numeric'}),
            }).format(d)
        }
        const text = dp.from && dp.to && dp.from !== dp.to ? `${fmt(dp.from)} 至 ${fmt(dp.to)}` : fmt(dp.from || dp.to!)
        const overdue = Boolean(dp.from) && dp.from! < Date.now() && !dp.to
        return (
            <span className={classnames('propValue', !asChip && 'bg-transparent px-0', overdue && 'text-[#d8002e]', className)} title={text}>
                <Icon name='date' size={13} className='mr-1 shrink-0'/>
                <span className='truncate'>{text}</span>
            </span>
        )
    }
    case 'checkbox': {
        const checked = raw === 'true'
        return (
            <span className={classnames('propValue', !asChip && 'bg-transparent px-0', !checked && 'opacity-50', className)}>
                <Icon name='checkbox' size={14} className='mr-1 shrink-0'/>
                {checked ? '已勾选' : '未勾选'}
            </span>
        )
    }
    case 'person': {
        const user = usersById[String(raw)]
        if (!user) {
            return null
        }
        return (
            <span className={classnames('propValue', !asChip && 'bg-transparent px-0', className)}>
                <span className='mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-black/10 text-[9px] font-semibold'>
                    {avatarInitial(user)}
                </span>
                <span className='truncate'>{user.username}</span>
            </span>
        )
    }
    case 'multiPerson': {
        const ids = parseArrayValue(raw)
        if (!ids.length) {
            return null
        }
        return (
            <span className={classnames('flex items-center gap-1', className)}>
                {ids.map((id) => (
                    <span
                        key={id}
                        className='flex h-5 w-5 items-center justify-center rounded-full bg-black/10 text-[9px] font-semibold'
                        title={usersById[id]?.username || id}
                    >
                        {avatarInitial(usersById[id])}
                    </span>
                ))}
            </span>
        )
    }
    case 'number': {
        if (isEmpty) {
            return null
        }
        return <span className={classnames('tabular-nums', className)}>{String(raw)}</span>
    }
    case 'url': {
        const url = String(raw || '')
        if (!url) {
            return null
        }
        return (
            <a
                className={classnames('truncate', className)}
                href={url.startsWith('http') ? url : `https://${url}`}
                target='_blank'
                rel='noreferrer'
            >
                {url}
            </a>
        )
    }
    case 'email': {
        const mail = String(raw || '')
        return mail ? <a href={`mailto:${mail}`} className={className}>{mail}</a> : null
    }
    case 'createdTime':
        return <span className={className}>{new Intl.DateTimeFormat(Utils.getLocale(), {month: 'short', day: 'numeric', year: 'numeric'}).format(new Date(card.createAt))}</span>
    case 'updatedTime':
        return <span className={className}>{new Intl.DateTimeFormat(Utils.getLocale(), {month: 'short', day: 'numeric', year: 'numeric'}).format(new Date(card.updateAt))}</span>
    case 'createdBy': {
        const user = usersById[card.createdBy]
        return user ? <span className={className}>{user.username}</span> : null
    }
    case 'updatedBy': {
        const user = usersById[card.modifiedBy]
        return user ? <span className={className}>{user.username}</span> : null
    }
    case 'file':
        return null
    default: {
        const text = typeof raw === 'string' ? raw : ''
        return text ? <span className={classnames('truncate', className)} title={text}>{text}</span> : null
    }
    }
}

/** The empty placeholder shown in a cell/property row with no value. */
export function EmptyProperty({template}: {template: IPropertyTemplate}): ReactElement {
    return (
        <span className='flex items-center gap-2 opacity-50'>
            <Icon name={propertyIcon(template)} size={14}/>
            <span className='truncate'>{propertyDisplayName(template)}</span>
        </span>
    )
}
